import { useState } from 'react'
import { useWebhookDeliveries } from '@/hooks/use-outbound-webhooks'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Clock } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { EventDetailsDialog } from './event-details-dialog'
import { fromWebhookDelivery, EVENT_LABELS } from './event-constants'
import { StatusBadge } from './event-ui'

type PlatformFilter = 'all' | 'webhook' | 'klaviyo'

export function WebhookDeliveryTable() {
  const [platform, setPlatform] = useState<PlatformFilter>('all')
  const platformQuery = platform === 'all' ? undefined : platform
  const { data: deliveries, isLoading } = useWebhookDeliveries(20, 0, platformQuery)

  if (isLoading) {
    return <WebhookDeliverySkeleton />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Histórico de envios</h2>
        <Select value={platform} onValueChange={(v) => setPlatform(v as PlatformFilter)}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Filtrar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="webhook">Notificações</SelectItem>
            <SelectItem value="klaviyo">Klaviyo</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Quando</TableHead>
            <TableHead>Destino</TableHead>
            <TableHead>Evento</TableHead>
            <TableHead>URL</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>HTTP</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveries?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-16 text-muted-foreground">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Clock className="h-8 w-8 text-muted-foreground/50" />
                  <p>Nenhum envio registrado ainda</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            deliveries?.map((delivery) => (
              <TableRow key={delivery.id}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(delivery.createdAt), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </TableCell>
                <TableCell>
                  <PlatformBadge platform={delivery.platform} />
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {EVENT_LABELS[delivery.eventType] || delivery.eventType}
                  </span>
                </TableCell>
                <TableCell
                  className="max-w-50 truncate text-xs font-mono text-muted-foreground"
                  title={delivery.url || undefined}
                >
                  {delivery.url || (
                    <span className="text-muted-foreground italic">Klaviyo API</span>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge status={delivery.status} />
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {delivery.responseCode ? (
                    <span
                      className={
                        delivery.responseCode >= 200 && delivery.responseCode < 300
                          ? 'text-green-600 font-bold'
                          : 'text-red-600 font-bold'
                      }
                    >
                      {delivery.responseCode}
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <EventDetailsDialog event={fromWebhookDelivery(delivery)} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function PlatformBadge({ platform }: { platform: 'webhook' | 'klaviyo' }) {
  if (platform === 'klaviyo') {
    return (
      <Badge variant="secondary" className="bg-[#00D4AA]/10 text-[#00D4AA] hover:bg-[#00D4AA]/20">
        Klaviyo
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20">
      Notificação
    </Badge>
  )
}

function WebhookDeliverySkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="border rounded-md bg-card shadow-sm">
        <div className="p-4 space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between gap-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
